import '@sass/flightdata/flightdata.scss';
import { copyToClipboard, documentReady, gebi, l, lreplace, shareClick } from '@js/utils/toolbox';
import { renderMultiCityLineGraph, renderMultiCityYearComparisonGraph, renderYearComparisonGraph } from '@js/components/priceGraphs';
import FlightDataCitySelector from '@/src/vue/components/FlightDataCitySelector/FlightDataCitySelector.vue';
import { createVueApp } from '@/src/vue/utils/createVueApp';
import { toaster } from '@js/components/toast';

const CHIP_COLORS = [
	'#31527A', // blue-900
	'#FBCC2C', // gold-500
	'#34AD2D', // green-500
	'#E83151', // red-500
	'#F5A623', // orange-300
	'#AF77BF', // purple-300
	'#2E5B8B', // blue-800
	'#FCDB6B', // gold-300
	'#34594c', // green-900
	'#FF5756', // red-300
	'#6E6E6E', // grey-700
	'#53658C', // blue-700
	'#9AD897', // green-300
	'#A53125', // red-700
	'#8D949E', // grey-600
	'#60779E', // blue-600
	'#3D9C88', // green-700
	'#429CFF' // blue-500
];

const TOP_POPULARITY_PAGE_SIZE = 10;

const { countries = [] } = js_params?.module?.FlightData ?? {};
const allCities = countries.flatMap(co => co.cities);

const flightdataPage = {
	activeCharts: [],
	topPopularityCharts: {},
	topPopularityPages: {},
	fromSelect: null,
	citySelectorEl: null,
	scrollAnchor: null,

	/**
	 * Bind events and mount the city selector
	 */
	init () {
		this.fromSelect = gebi('flightdata-from-select');
		this.citySelectorEl = gebi('flightdata-city-selector');
		this.scrollAnchor = window.location.hash ? window.location.hash.slice(1) : null;

		this.citySelectorEl?.addEventListener('fd:selected', (ev) => {
			this.citySelectorEl._fdSelected = ev.detail;
			this.syncUrl(ev.detail);
			this.renderCityCards(ev.detail);
			this.renderGraphs(ev.detail);
		});

		this.fromSelect?.addEventListener('change', () => {
			const current = this.citySelectorEl?._fdSelected ?? [];
			this.syncUrl(current);
			if (current.length) {
				this.renderGraphs(current);
			}
		});

		this.restoreFromParam();
		this.renderTopPopularityGraphs();

		if (this.scrollAnchor) {
			this.scrollToAnchor();
		}

		// mount after listeners so fd:selected fired from onMounted is caught
		createVueApp(FlightDataCitySelector, { countries, initialSelected: this.getCitiesFromUrl(), chipColors: CHIP_COLORS }).mount('#flightdata-city-selector');

		document.body.addEventListener('click', (ev) => {
			const card = ev.target.closest('.abCard--flightData[data-settings]');
			if (card) {
				window.location.href = card.getAttribute('data-settings');
				return;
			}

			const shareBtn = ev.target.closest('.shareBtn');
			if (shareBtn) {
				this.shareGraph(shareBtn);
			}
		});
	},

	shareGraph (shareBtn) {
		const hash = shareBtn.closest('section')?.getAttribute('id') ?? '';
		const shareUrl = window.location.origin + window.location.pathname + window.location.search + '#' + hash;

		shareClick({ title: globalThis.js_params?.domain_title, url: shareUrl }).catch((error) => {
			if (error.name === 'AbortError') {
				return;
			}
			copyToClipboard(shareUrl).then(() => {
				toaster.newToast(l('showresult.share.linkCopied'), 'info', 'top');
			});
		});
	},

	scrollToAnchor () {
		const target = gebi(this.scrollAnchor);

		target?.scrollIntoView({ block: 'start' });
		this.scrollAnchor = null;
	},

	/**
	 * Preselect the "from" dropdown based on the ?from= URL param
	 */
	restoreFromParam () {
		const fromParam = new URLSearchParams(window.location.search).get('from');
		if (fromParam && this.fromSelect) {
			const match = [...this.fromSelect.options].find(o => o.value === fromParam);
			if (match) {
				this.fromSelect.value = match.value;
			}
		}
	},

	/**
	 * Keep the ?cities= and ?from= URL params in sync with the current selection
	 */
	syncUrl (selected) {
		const params = new URLSearchParams(window.location.search);
		if (selected.length) {
			params.set('cities', selected.map(c => c.cityId).join(','));
		} else {
			params.delete('cities');
		}
		if (this.fromSelect?.value) {
			params.set('from', this.fromSelect.value);
		} else {
			params.delete('from');
		}
		const qs = params.toString();
		history.replaceState(null, '', `${qs ? `?${qs}` : window.location.pathname}${window.location.hash}`);
	},

	/**
	 * Resolve the ?cities= URL param into city objects
	 */
	getCitiesFromUrl () {
		const params = new URLSearchParams(window.location.search);
		const raw = params.get('cities') ?? '';
		if (!raw) {
			return [];
		}
		const cityIds = raw.split(',').map(s => parseInt(s.trim(), 10)).filter(Boolean);
		return cityIds.map(id => allCities.find(c => c.cityId === id)).filter(Boolean);
	},

	/**
	 * Fetch and render the price graphs for the currently selected cities
	 */
	async renderGraphs (selected) {
		if (!selected.length) {
			this.clearGraphs();
			return;
		}

		const cities = selected.map(c => ({ iata: c.iata, cityId: c.cityId, cityName: c.name }));
		const fromCityId = this.fromSelect?.value ? parseInt(this.fromSelect.value, 10) : null;

		let data;
		try {
			const res = await fetch('/api/FlightData/getGraphData', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ cities, fromCityId })
			});
			data = await res.json();
		} catch {
			return;
		}

		this.activeCharts.forEach(c => c.destroy());
		this.activeCharts = [];

		const { datasets = [] } = data;
		if (!datasets.length) {
			this.clearGraphs();
			return;
		}

		this.renderSection('flightdata-price-graph', datasets, 'priceGraph', false);
		this.renderSection('flightdata-weekday-graph', datasets, 'weekdayGraph', false);
		this.renderSection('flightdata-lead-time-graph', datasets, 'leadTimeGraph', true);
		this.renderSection('flightdata-year-comparison-graph', datasets, 'yearComparisonGraph', false);
		this.renderSection('flightdata-popularity-comparison-graph', datasets, 'popularityComparisonGraph', false);

		this.scrollToAnchor();
	},

	renderSection (containerId, datasets, graphKey, reverse) {
		const el = document.getElementById(containerId);
		if (!el) {
			return;
		}

		const section = el.closest('.price-graph-section');

		const keyed = datasets
			.map((ds, i) => ({ ...ds[graphKey], cityName: ds.cityName, iata: ds.iata, color: CHIP_COLORS[i % CHIP_COLORS.length] }))
			.filter(ds => ds?.points?.length);

		if (!keyed.length) {
			section?.classList.add('hidden');
			el.innerHTML = '';
			return;
		}

		section?.classList.remove('hidden');
		el.innerHTML = '';

		const chart = ['yearComparisonGraph', 'popularityComparisonGraph'].includes(graphKey)
			? renderMultiCityYearComparisonGraph(el, keyed)
			: renderMultiCityLineGraph(el, keyed, { reverse });

		if (chart) {
			this.activeCharts.push(chart);
		}
	},

	clearGraphs () {
		this.activeCharts.forEach(c => c.destroy());
		this.activeCharts = [];
		document.querySelectorAll('.price-graph-section:not(.price-graph-section--static)').forEach(section => section.classList.add('hidden'));
	},

	renderTopPopularityGraphs () {
		['popularityRisersGraph', 'popularityFallersGraph'].forEach((graphId) => {
			const el = gebi(graphId);
			const data = globalThis.js_params?.[`${graphId}Data`];

			if (!el || !data) {
				return;
			}

			const chart = renderYearComparisonGraph(el, data, { popularity: true, rotateLabels: true, clampToZero: true, visibleCount: TOP_POPULARITY_PAGE_SIZE });
			this.topPopularityCharts[graphId] = chart;
			this.showTopPopularityPage(graphId, 0);
			chart.options.onResize = () => requestAnimationFrame(() => this.linkTopPopularityLabels(graphId));

			document.querySelectorAll(`.box--price-graph__pager[data-graph="${graphId}"] [data-step]`).forEach(step => step.addEventListener('click', () => {
				this.showTopPopularityPage(graphId, this.topPopularityPages[graphId] + Number(step.dataset.step));
			}));
		});
	},

	/** invisible anchors over the canvas-painted labels, laid out from the geometry the scale drew with */
	linkTopPopularityLabels (graphId) {
		const LABEL_HIT_MARGIN = 6;
		const chart = this.topPopularityCharts[graphId];
		const { points } = globalThis.js_params[`${graphId}Data`];
		const scale = chart.scales.x;
		const container = chart.canvas.parentElement;

		container.querySelectorAll('.box--price-graph__label-link').forEach(link => link.remove());

		(scale._labelItems ?? []).forEach((item, index) => {
			const point = points[scale.ticks[index].value];

			if (!point.url) {
				return;
			}

			chart.ctx.font = item.font.string;
			const width = chart.ctx.measureText(item.label).width + 2 * LABEL_HIT_MARGIN;
			const height = item.font.lineHeight + 2 * LABEL_HIT_MARGIN;
			const shift = { right: LABEL_HIT_MARGIN - width, center: -width / 2, left: -LABEL_HIT_MARGIN }[item.options.textAlign];

			const link = document.createElement('a');
			link.className = 'box--price-graph__label-link';
			link.href = point.url;
			link.ariaLabel = item.label;
			link.style.left = `${chart.canvas.offsetLeft + item.options.translation[0]}px`;
			link.style.top = `${chart.canvas.offsetTop + item.options.translation[1]}px`;
			link.style.width = `${width}px`;
			link.style.height = `${height}px`;
			link.style.transform = `rotate(${item.options.rotation}rad) translate(${shift}px, ${item.textOffset - height / 2}px)`;
			container.append(link);
		});
	},

	showTopPopularityPage (graphId, page) {
		const { points } = globalThis.js_params[`${graphId}Data`];
		const pageCount = Math.ceil(points.length / TOP_POPULARITY_PAGE_SIZE);
		const currentPage = Math.min(Math.max(page, 0), pageCount - 1);
		const from = currentPage * TOP_POPULARITY_PAGE_SIZE;
		const to = Math.min(from + TOP_POPULARITY_PAGE_SIZE, points.length);

		this.topPopularityPages[graphId] = currentPage;

		const chart = this.topPopularityCharts[graphId];
		chart.options.scales.x.min = from;
		chart.options.scales.x.max = to - 1;
		chart.update();
		// the label geometry the links are laid out from exists only after the next frame's draw
		requestAnimationFrame(() => this.linkTopPopularityLabels(graphId));

		const pager = document.querySelector(`.box--price-graph__pager[data-graph="${graphId}"]`);
		pager.classList.toggle('hidden', pageCount < 2);
		// lreplace has no word boundary, so no placeholder may prefix another ($to would eat $total)
		pager.querySelector('.box--price-graph__pager-range').textContent = lreplace('flightdataPage.topPopularity.range', { from: from + 1, to, count: points.length });
		pager.querySelectorAll('[data-step]').forEach((step) => {
			step.disabled = Number(step.dataset.step) < 0 ? currentPage === 0 : currentPage >= pageCount - 1;
		});
	},

	/**
	 * Render the city page link cards for the currently selected cities
	 */
	renderCityCards (selected) {
		const container = gebi('cityCards');
		if (!container) {
			return;
		}

		const section = container.closest('.container--most-searched');
		const citiesWithUrl = selected.filter(city => city.url);

		section?.classList.toggle('empty', !citiesWithUrl.length);

		container.innerHTML = '';

		citiesWithUrl.forEach((city) => {
			const block = document.createElement('div');
			block.className = 'mostSearchedBlock';

			const link = document.createElement('a');
			link.className = 'mostSearchedBlock__link';
			link.href = city.url;
			link.target = '_blank';
			link.rel = 'noopener';

			const hero = document.createElement('div');
			hero.className = 'mostSearchedBlock__hero';

			const img = document.createElement('img');
			img.className = 'mostSearchedBlock__image';
			img.loading = 'lazy';
			img.src = city.image ?? '';
			img.width = 150;
			img.height = 135;
			img.alt = city.name;
			hero.append(img);

			const info = document.createElement('div');
			info.className = 'mostSearchedBlock__info';

			const destination = document.createElement('div');
			destination.className = 'mostSearchedBlock__destination';

			const cityRow = document.createElement('div');
			cityRow.className = 'mostSearchedBlock__city';
			const citySpan = document.createElement('span');
			citySpan.textContent = city.name;
			cityRow.append(citySpan);

			const countryRow = document.createElement('div');
			countryRow.className = 'mostSearchedBlock__country';
			const countrySpan = document.createElement('span');
			countrySpan.textContent = city.countryName ?? '';
			countryRow.append(countrySpan);

			destination.append(cityRow, countryRow);
			info.append(destination);
			link.append(hero, info);
			block.append(link);
			container.append(block);
		});
	}
};

documentReady(function () {
	window.flightdataPage = flightdataPage;
	flightdataPage.init();
});
